import { useQuery } from '@tanstack/react-query';
import { getSubmissionById } from '../../../api/submissions/submissions.api';
import { getQuoteDetails, getQuoteP100 } from '../../../api/quotes.api';
import {
  getPolicyDetails,
  getPolicyDetailsP100,
} from '../../../api/policies.api';
import { getClaim } from '../../../api/claims/claim.api';
import { getAccountDetails } from '../../../api/accounts.api';
import { getMessageByMsgId } from '../../../api/cmail.api';
import { fetchAgencyDetails } from '../../../agencies/AgencyService';
import { queries } from '../../../queries';
import type { EntityType } from '../types';
import {
  getEntityConfig,
  extractFields,
} from '../components/context-chips/entityConfig';

interface EntityLabel {
  label: string;
  number?: string;
  name?: string;
  resolvedType?: string;
}

export function buildLabel(number?: string, name?: string): string {
  if (number && name) return `${number} - ${name}`;
  return name || number || '';
}

/**
 * Some APIs (P100/P250) wrap the entity inside { data: { ...fields } },
 * while others (PrimeX) return a flat object after transforms.
 * This helper safely unwraps the inner `.data` when present.
 */
export function unwrap(
  data: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (
    data?.data &&
    typeof data.data === 'object' &&
    !Array.isArray(data.data)
  ) {
    return data.data as Record<string, unknown>;
  }
  return data;
}

export function extractProductFromRoute(route: string): string | undefined {
  const inboxMatch = route.match(/\/inbox\/[\w-]+\/([\w-]+)/);
  if (inboxMatch) return inboxMatch[1];

  const submissionQuoteMatch = route.match(
    /\/submissions\/(?:quotes|requestQuote)\/([\w-]+)\//
  );
  if (submissionQuoteMatch) return submissionQuoteMatch[1];

  const quoteDetailMatch = route.match(/\/quote\/details?\/[\w-]+\/([\w-]+)/);
  if (quoteDetailMatch) return quoteDetailMatch[1];

  const quoteEditMatch = route.match(/\/quote\/edit\/[\w-]+\/([\w-]+)/);
  if (quoteEditMatch) return quoteEditMatch[1];

  const ratingViewMatch = route.match(/\/policies\/rating\/[\w-]+\/([\w-]+)/);
  if (ratingViewMatch) return ratingViewMatch[1];

  const carrierRatingMatch = route.match(
    /^\/(?:carrier|reinsurer)\/policies\/(?!detail\/)[\w-]+\/([\w-]+)/
  );
  if (carrierRatingMatch) return carrierRatingMatch[1];

  const policyDetailMatch = route.match(
    /\/(?:policies|policy)\/detail\/([^/]+)\//
  );
  if (policyDetailMatch) return policyDetailMatch[1];

  return undefined;
}

/**
 * Fetch API data and extract number/name using the entity config's field mappings.
 */
function extractFromResponse(
  raw: unknown,
  entityType: string
): { number: string; name: string } {
  const data = unwrap(raw as Record<string, unknown>);
  const config = getEntityConfig(entityType);
  if (!config || !data) return { number: '', name: '' };
  return extractFields(data, config);
}

async function resolveQuote(
  entityId: string,
  product?: string
): Promise<EntityLabel> {
  if (product) {
    try {
      const { data: raw } = await getQuoteDetails(entityId, product);
      const { number, name } = extractFromResponse(raw, 'quote');
      return { label: buildLabel(number, name), number, name };
    } catch {
      // fall through to generic
    }
  }
  try {
    const { data: raw } = await getQuoteP100(entityId);
    const { number, name } = extractFromResponse(raw, 'quote');
    return { label: buildLabel(number, name), number, name };
  } catch {
    return { label: '' };
  }
}

async function resolvePolicy(
  entityId: string,
  product?: string
): Promise<EntityLabel> {
  if (product) {
    try {
      const { data: raw } = await getPolicyDetails(entityId, product);
      const { number, name } = extractFromResponse(raw, 'policy');
      return { label: buildLabel(number, name), number, name };
    } catch {
      // fall through to generic
    }
  }
  try {
    const { data: raw } = await getPolicyDetailsP100(entityId);
    const { number, name } = extractFromResponse(raw, 'policy');
    return { label: buildLabel(number, name), number, name };
  } catch {
    return { label: '' };
  }
}

async function resolveEntity(
  entityType: EntityType,
  entityId: string,
  route?: string
): Promise<EntityLabel> {
  const product = route ? extractProductFromRoute(route) : undefined;

  switch (entityType) {
    case 'uw-review': {
      const quoteResult = await resolveQuote(entityId, product);
      if (quoteResult.number) return { ...quoteResult, resolvedType: 'quote' };

      try {
        const { data: rawSub } = await getSubmissionById(entityId);
        const { number, name } = extractFromResponse(rawSub, 'submission');
        if (number) {
          return {
            label: buildLabel(number, name),
            number,
            name,
            resolvedType: 'submission',
          };
        }
      } catch {
        /* not a submission */
      }

      const policyResult = await resolvePolicy(entityId, product);
      if (policyResult.number)
        return { ...policyResult, resolvedType: 'policy' };

      if (quoteResult.label) return { ...quoteResult, resolvedType: 'quote' };

      return { label: '' };
    }

    case 'quote':
      return {
        ...(await resolveQuote(entityId, product)),
        resolvedType: 'quote',
      };

    case 'submission': {
      if (product) {
        const quoteResult = await resolveQuote(entityId, product);
        if (quoteResult.number)
          return { ...quoteResult, resolvedType: 'quote' };
      }
      try {
        const { data: rawSub } = await getSubmissionById(entityId);
        const { number, name } = extractFromResponse(rawSub, 'submission');
        return {
          label: buildLabel(number, name),
          number,
          name,
          resolvedType: 'submission',
        };
      } catch {
        return { label: '', resolvedType: 'submission' };
      }
    }

    case 'policy':
      return {
        ...(await resolvePolicy(entityId, product)),
        resolvedType: 'policy',
      };

    case 'claim': {
      try {
        const { data: rawClaim } = await getClaim(entityId);
        const { number, name } = extractFromResponse(rawClaim, 'claim');
        return {
          label: buildLabel(number, name),
          number,
          name,
          resolvedType: 'claim',
        };
      } catch {
        return { label: '', resolvedType: 'claim' };
      }
    }

    case 'account': {
      try {
        const { data: rawAcct } = await getAccountDetails(entityId);
        const { name } = extractFromResponse(rawAcct, 'account');
        return { label: name, name, resolvedType: 'account' };
      } catch {
        return { label: '', resolvedType: 'account' };
      }
    }

    case 'binder': {
      // Binders share the quote detail route — try resolving as a quote
      const binderResult = await resolveQuote(entityId, product);
      if (binderResult.number || binderResult.name) {
        return { ...binderResult, resolvedType: 'binder' };
      }
      return { label: '', resolvedType: 'binder' };
    }

    case 'cmail': {
      try {
        const { data: rawMsg } = await getMessageByMsgId({
          params: { msgId: entityId },
        });
        const { name } = extractFromResponse(rawMsg, 'cmail');
        return { label: name, name, resolvedType: 'cmail' };
      } catch {
        return { label: '', resolvedType: 'cmail' };
      }
    }

    case 'agency': {
      try {
        const { data: rawAgency } = await fetchAgencyDetails(entityId);
        const { name } = extractFromResponse(rawAgency, 'agency');
        return { label: name, name, resolvedType: 'agency' };
      } catch {
        return { label: '', resolvedType: 'agency' };
      }
    }

    default:
      return { label: '' };
  }
}

export function useEntityLabel(
  entityType: EntityType | undefined,
  entityId: string | undefined,
  route?: string
) {
  const isAccount = entityType === 'account';

  // For accounts, subscribe to the SAME query the page uses (via useAccountDetails)
  // so React Query deduplicates the API call instead of making a redundant one.
  const accountDetail = queries.accounts.detail(entityId!);
  const accountQuery = useQuery(accountDetail.queryKey, accountDetail.queryFn, {
    enabled: isAccount && !!entityId,
    select: (data: unknown): EntityLabel => {
      const { name } = extractFromResponse(data, 'account');
      return { label: name || '', name, resolvedType: 'account' };
    },
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  const genericQuery = useQuery(
    ['askMoo', 'entityLabel', entityType, entityId],
    () => resolveEntity(entityType!, entityId!, route),
    {
      enabled: !!entityType && !!entityId && !isAccount,
      staleTime: 10 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 1,
    }
  );

  return isAccount ? accountQuery : genericQuery;
}
